/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import type { AGENT_AVATAR_CLIP_PATH as AGENT_AVATAR_CLIP_PATH_TYPE } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { SuggestionProfile } from '../../aiSuggestionsPluginType';

/**
 * A profile in the avatar stack. Only agents appear here — human authors are deliberately
 * excluded. A slot with no `profile` means the suggestion had no `createdByAgent`, and renders the
 * generic Rovo hex logo instead.
 */
export type SuggestionAvatarProfile = { profile?: SuggestionProfile };

const AGENT_AVATAR_CLIP_PATH =
	'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)' satisfies typeof AGENT_AVATAR_CLIP_PATH_TYPE;

const styles = cssMap({
	// Sized to match `AgentAvatar`'s own `xxsmall` footprint, so the manually-drawn ring below
	// sits flush against it.
	avatarRingWrapper: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
	},
	// Hand-drawn hexagon ring for agents — `AgentAvatar`'s own `showBorder` inset is too thin at
	// `xxsmall`. `inset: -2px` centers this via exact pixel math off `avatarRingWrapper`'s own
	// box, avoiding the sub-pixel drift a transform-based centering approach could introduce.
	agentAvatarRing: {
		position: 'absolute',
		zIndex: 0,
		inset: '-2px',
		// Matches the 2px box-shadow ring `@atlaskit/avatar` draws by default, so this stack lines up with
		// avatars elsewhere. `color.border.inverse` reads as white in light mode against the avatar's bold
		// background, and flips to a dark ring in dark mode for the same contrast.
		backgroundColor: token('color.border.inverse'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	avatarRingContent: {
		position: 'relative',
		zIndex: 1,
		// Explicit size + centering forces the content dead-center regardless of its own box
		// model. `fontSize: 0` strips any line-box height it could otherwise inherit, which
		// would offset it via baseline alignment instead of true centering.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: token('space.200'),
		width: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Not real text; collapses the phantom inline line-box, no typography token applies.
		fontSize: 0,
	},
});

/**
 * One agent avatar's ring + content — the specific agent, or the generic Rovo fallback when the
 * suggestion has no agent profile. Used by `SuggestionAvatarIndicator` to render each entry in the
 * stack.
 */
export function SuggestionAvatar({ profile }: SuggestionAvatarProfile): JSX.Element {
	return (
		<span css={styles.avatarRingWrapper}>
			<span css={styles.agentAvatarRing} />
			<span css={styles.avatarRingContent}>
				{profile ? (
					<AgentAvatar
						imageUrl={profile.picture?.trim() || undefined}
						agentIdentityAccountId={profile.accountId}
						name={profile.name}
						label={profile.name}
						size="xxsmall"
						showBorder={false}
					/>
				) : (
					<RovoHexIcon size="xxsmall" />
				)}
			</span>
		</span>
	);
}
